import React, { useState } from "react";
import { useLang } from "../../context/LangContext";
import { useAuth } from "../../context/AuthContext";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { KeyRound, User, Save } from "lucide-react";

export default function AdminSettings() {
  const { t } = useLang();
  const { user } = useAuth();
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.current_password || !form.new_password) return toast.error(t("fields_required"));
    if (form.new_password.length < 6) return toast.error(t("password_too_short"));
    if (form.new_password !== form.confirm) return toast.error(t("passwords_do_not_match"));
    setLoading(true);
    try {
      await api.post("/auth/change-password", {
        current_password: form.current_password,
        new_password: form.new_password,
      });
      toast.success(t("password_reset_done"));
      setForm({ current_password: "", new_password: "", confirm: "" });
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };

  return (
    <div className="space-y-4 max-w-lg" data-testid="admin-settings-page">
      <h1 className="text-2xl sm:text-3xl font-semibold">{t("admin_settings")}</h1>

      <div className="bg-white border border-[#E5E1D8] rounded-2xl p-6 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-[#F3EFE7] text-[#2A5948] flex items-center justify-center"><User size={18} /></div>
          <div>
            <div className="font-semibold">{user?.name}</div>
            <div className="text-xs text-[#5C6053]" dir="ltr">{user?.phone}</div>
          </div>
        </div>
      </div>

      <form onSubmit={submit} className="bg-white border border-[#E5E1D8] rounded-2xl p-6 shadow-sm space-y-4">
        <div className="flex items-center gap-2 text-[#2A5948] font-medium">
          <KeyRound size={16} /> {t("change_password")}
        </div>
        <PwField label={t("current_password")} value={form.current_password} onChange={(v) => setForm({ ...form, current_password: v })} testid="admin-current-password" />
        <PwField label={t("new_password")} value={form.new_password} onChange={(v) => setForm({ ...form, new_password: v })} testid="admin-new-password" />
        <PwField label={t("confirm_password")} value={form.confirm} onChange={(v) => setForm({ ...form, confirm: v })} testid="admin-confirm-password" />
        <button data-testid="admin-change-password-btn" disabled={loading} type="submit" className="brand-btn px-4 py-2 rounded-lg text-sm inline-flex items-center gap-2 disabled:opacity-60">
          <Save size={14} /> {loading ? "..." : t("save")}
        </button>
      </form>
    </div>
  );
}

function PwField({ label, value, onChange, testid }) {
  return (
    <label className="block">
      <div className="text-xs font-medium text-[#5C6053] mb-1">{label}</div>
      <input
        data-testid={testid}
        type="password"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full border border-[#E5E1D8] bg-[#F9F6F0] rounded-xl px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#2A5948]/40"
      />
    </label>
  );
}
